import React, { useState } from 'react';
import { observer } from 'mobx-react';
import { Button, Col, Input, List, Popconfirm, Row, notification } from 'antd';
import {
  DeleteOutlined,
  EditOutlined,
  ProfileOutlined,
  QuestionCircleOutlined,
  CheckOutlined,
  StopOutlined
} from '@ant-design/icons';
import { toJS } from 'mobx';
import { Can } from '@app/modules/Auth';
import { TemperatureSlider } from '@app/modules/Profiles/Temperature/components/TemperatureSlider';
import { Profile } from '@app/modules/Profiles/Temperature/store';
import { useStores } from '@app/hooks/stores';
import api from '../api';

declare type ProfileItemProps = {
  item: Profile;
};

const ProfileItem: React.FC<ProfileItemProps> = ({ item }) => {
  const { profilesStore } = useStores();
  const [deleteConfirmationsMap, setDeleteConfirmationVisibility] = useState<{ [key: number]: boolean }>({});
  const [editingItemsMap, setEditingMode] = useState<{ [key: number]: boolean }>({});

  const toggleEditingOnItem = (item, forcedValue) => {
    setEditingMode({
      ...editingItemsMap,
      ...{
        [item.id]: typeof forcedValue !== 'undefined' ? forcedValue : !editingItemsMap[item.id]
      }
    });
  };

  const onEditMode = (item: Profile) => {
    profilesStore.saveBackupItem(item);
    toggleEditingOnItem(item, true);
  };

  const getIsEditing = item => !item.id || editingItemsMap[item.id];

  const onCancelEditing = (item: Profile) => {
    if (item.id) {
      profilesStore.restoreBackedUpItem(item);
    } else {
      // remove blank item
      profilesStore.removeProfile(item);
    }
    toggleEditingOnItem(item, false);
  };

  const onChangeBaseTemp = (item: Profile, baseTempValue) => {
    item.baseTemp = baseTempValue;
  };

  const onChangePrOffTemp = (item: Profile, prOffTempValue) => {
    item.prOffTemp = prOffTempValue;
  };

  const onChangeProfileName = (item: Profile, event) => {
    const name = event.target.value;
    item.name = name;
  };

  const onSave = async (item: Profile) => {
    try {
      if (item.id) {
        const { id, baseTemp, prOffTemp, name } = item;
        // saving
        await api.editProfile(id, { baseTemp, prOffTemp, name });
        notification.success({
          message: 'Profile saved!'
        });
      } else {
        if (!item.name) {
          item.name = `${item.baseTemp}/${item.prOffTemp}`;
        }
        const id = await api.addProfile(toJS(item));
        item.id = id;
        notification.success({
          message: 'Profile added successfully!'
        });
      }
    } catch (err) {
      debugger;
    }

    toggleEditingOnItem(item, false);
  };

  const onProfileDelete = async (item: Profile) => {
    try {
      await api.deleteProfile(item.id);
      profilesStore.removeProfile(item);
      notification.success({
        message: `"${item.name}" deleted !`
      });
    } catch (e) {
      notification.error({
        message: 'Error',
        description: `Error when delete profile! ${e.message}`
      });
    }
  };

  const handleDeleteConfirmationVisibility = (visible, item) => {
    setDeleteConfirmationVisibility({
      ...deleteConfirmationsMap,
      ...{
        [item.id]: visible
      }
    });
  };

  return (
    <List.Item
      key={item.id}
      actions={
        getIsEditing(item)
          ? [
              <Can
                action="put:profiles/temperature"
                yes={() => (
                  <Button type="link" icon={<CheckOutlined />} onClick={e => onSave(item)}>
                    Save
                  </Button>
                )}
                no={() => (
                  <Button type="link" icon={<CheckOutlined />} disabled>
                    Save
                  </Button>
                )}
              />,
              <Button type="link" icon={<StopOutlined />} onClick={e => onCancelEditing(item)}>
                Cancel
              </Button>
            ]
          : [
              <Can
                action="put:profiles/temperature"
                yes={() => (
                  <Button type="link" icon={<EditOutlined />} onClick={e => onEditMode(item)}>
                    Edit
                  </Button>
                )}
                no={() => (
                  <Button type="link" disabled icon={<EditOutlined />}>
                    Edit
                  </Button>
                )}
              />,
              <Can
                action="delete:profiles/temperature"
                yes={() => (
                  <Popconfirm
                    title={`Are you sure you want to delete "${item.name}" profile?`}
                    icon={<QuestionCircleOutlined style={{ color: 'red' }} />}
                    open={deleteConfirmationsMap[item.id]}
                    onOpenChange={visible => handleDeleteConfirmationVisibility(visible, item)}
                    onConfirm={() => onProfileDelete(item)}
                    okText="Yes"
                    cancelText="No"
                  >
                    <Button type="link" icon={<DeleteOutlined />}>
                      Delete
                    </Button>
                  </Popconfirm>
                )}
                no={() => (
                  <Button type="link" disabled icon={<DeleteOutlined />}>
                    Delete
                  </Button>
                )}
              />
            ]
      }
    >
      <List.Item.Meta
        avatar={<ProfileOutlined />}
        title={
          getIsEditing(item) ? (
            <Input value={item.name} size="small" onChange={value => onChangeProfileName(item, value)} autoFocus />
          ) : (
            <strong>{item.name}</strong>
          )
        }
        description={
          <Row>
            <Col span={4}>
              <label>Base Temp.</label>
            </Col>
            <Col span={8}>
              <TemperatureSlider
                value={item.baseTemp}
                disabled={!getIsEditing(item)}
                onChange={value => onChangeBaseTemp(item, value)}
              />
            </Col>
            <Col span={4}>
              <label>Temperature (presence off)</label>
            </Col>
            <Col span={8}>
              <TemperatureSlider
                value={item.prOffTemp}
                disabled={!getIsEditing(item)}
                onChange={value => onChangePrOffTemp(item, value)}
              />
            </Col>
          </Row>
        }
      />
    </List.Item>
  );
};

export default observer(ProfileItem);
